import { useState } from 'react';
import { useEmergencyStore } from '@/stores/emergency-store';
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Building2,
  CheckCircle2,
  Clock,
  MapPin,
  Send,
  XCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

function formatSeniority(createdAt: string): string {
  const months = (new Date().getFullYear() - new Date(createdAt).getFullYear()) * 12
    + (new Date().getMonth() - new Date(createdAt).getMonth());
  if (months < 1) return 'nuevo';
  if (months < 12) return `${months}m cliente`;
  const y = Math.floor(months / 12), m = months % 12;
  return m > 0 ? `${y}a ${m}m` : `${y}a cliente`;
}

const SEVERITY_BADGE: Record<string, string> = {
  INFO: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  WARNING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  ALARM: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  CRITICAL: 'bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-200',
};

export function ArcEventDetail() {
  const {
    selectedArcEvent,
    selectArcEvent,
    acknowledgeArcEventInStore,
    resolveArcEventInStore,
    dispatchPatrolForArcEvent,
  } = useEmergencyStore();

  const [busy, setBusy] = useState<null | 'ack' | 'resolve' | 'dispatch'>(null);
  const [resolution, setResolution] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!selectedArcEvent) return null;
  const ev = selectedArcEvent;
  const isAcked = !!ev.acknowledged_at;
  const isDispatched = !!ev.ticket_id;

  const handleAck = async () => {
    setBusy('ack'); setError(null);
    try { await acknowledgeArcEventInStore(ev.id); }
    finally { setBusy(null); }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      setError('Describe brevemente cómo se resolvió el evento.');
      return;
    }
    setBusy('resolve'); setError(null);
    try {
      await resolveArcEventInStore(ev.id, resolution);
      // Store will null selectedArcEvent on success
    } finally { setBusy(null); }
  };

  const handleDispatch = async () => {
    setBusy('dispatch'); setError(null); setNotice(null);
    try {
      const result = await dispatchPatrolForArcEvent(ev.id);
      if (!result.success) {
        setError(result.reason ?? 'No se pudo despachar la patrulla.');
      } else if (result.billed) {
        setNotice(
          `Cuota de patrullas incluidas agotada este mes — se generó la factura ${result.invoiceNumber ?? ''} (Q${result.amount?.toFixed(2) ?? ''}) a nombre del propietario de la cuenta. Queda en borrador y solo se emite si la patrulla confirma salida (EN_ROUTE); se anula si el evento se cancela antes.`,
        );
      }
    } finally { setBusy(null); }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-800 flex items-center gap-3">
        <button
          onClick={() => selectArcEvent(null)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500"
          aria-label="Volver"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SEVERITY_BADGE[ev.severity] ?? SEVERITY_BADGE.INFO}`}>
              {ev.severity}
            </span>
            {isDispatched && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                Patrulla asignada
              </span>
            )}
            {!isAcked && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                Sin acuse
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mt-1">
            {ev.description_es ?? `CID ${ev.cid_code}`}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        {/* Event meta */}
        <section className="space-y-1.5">
          <div className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="font-medium">CID {ev.cid_code}{ev.cid_qualifier === 'E' ? 'E' : 'R'}</span>
            {ev.zone_no != null && <span className="text-xs text-gray-500">· Zona {ev.zone_no}</span>}
            {ev.zone_name && <span className="text-xs text-gray-500">· {ev.zone_name}</span>}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
            <Clock className="w-3.5 h-3.5" />
            Ocurrió {formatDistanceToNow(new Date(ev.occurred_at), { addSuffix: true, locale: es })}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500">
            Recibido {formatDistanceToNow(new Date(ev.received_at), { addSuffix: true, locale: es })}
          </div>
          {ev.acknowledged_at && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Acusado {formatDistanceToNow(new Date(ev.acknowledged_at), { addSuffix: true, locale: es })}
            </div>
          )}
        </section>

        {/* Site info */}
        <section className="rounded-lg bg-gray-50 dark:bg-slate-800/60 p-3 space-y-1.5">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <Building2 className="w-4 h-4" />
            <span className="font-semibold">{ev.site_name ?? 'Sitio sin nombre'}</span>
          </div>
          {(ev.account_number || (ev as any).account_tier) && (
            <div className="flex items-center gap-2 flex-wrap">
              {ev.account_number && (
                <p className="text-xs text-gray-500 dark:text-slate-400">Cuenta {ev.account_number}</p>
              )}
              {(ev as any).account_tier && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                  (ev as any).account_tier === 'A' ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-600' :
                  (ev as any).account_tier === 'B' ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-600' :
                  'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600'
                }`}>
                  Tier {(ev as any).account_tier}
                </span>
              )}
              {(ev as any).account_created_at && (
                <span className="text-[10px] text-gray-400 dark:text-slate-500">
                  {formatSeniority((ev as any).account_created_at)}
                </span>
              )}
            </div>
          )}
          {ev.site_address && (
            <p className="text-xs text-gray-600 dark:text-slate-300 flex items-start gap-1">
              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{ev.site_address}</span>
            </p>
          )}
          {ev.site_lat != null && ev.site_lng != null && (
            <a
              href={`https://www.google.com/maps?q=${ev.site_lat},${ev.site_lng}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
            >
              Abrir en Google Maps
            </a>
          )}
        </section>

        {/* Resolution input — only for non-dispatched events. Dispatched
            events should be closed via the patrol's ticket flow, not here. */}
        {!isDispatched && (
          <section>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Notas de resolución
            </label>
            <textarea
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Falsa alarma, prueba del cliente, etc."
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-slate-800 border border-transparent focus:border-amber-500 outline-none text-gray-900 dark:text-white"
            />
          </section>
        )}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{notice}</span>
          </div>
        )}
      </div>

      {/* Action footer */}
      <div className="border-t border-gray-200 dark:border-slate-800 p-3 space-y-2">
        {!isAcked && (
          <button
            disabled={busy !== null}
            onClick={handleAck}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
          >
            <Bell className="w-4 h-4" />
            {busy === 'ack' ? 'Acusando…' : 'Acusar evento'}
          </button>
        )}

        {!isDispatched && (
          <button
            disabled={busy !== null || ev.site_lat == null}
            onClick={handleDispatch}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            title={ev.site_lat == null ? 'El sitio no tiene coordenadas' : ''}
          >
            <Send className="w-4 h-4" />
            {busy === 'dispatch' ? 'Despachando…' : 'Despachar patrulla'}
          </button>
        )}

        {!isDispatched && (
          <button
            disabled={busy !== null}
            onClick={handleResolve}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" />
            {busy === 'resolve' ? 'Cerrando…' : 'Cerrar como falsa alarma'}
          </button>
        )}

        {isDispatched && (
          <p className="text-xs text-gray-500 dark:text-slate-400 text-center">
            Este evento ya tiene un ticket asignado a una patrulla. Gestiónalo desde la pestaña Alertas.
          </p>
        )}
      </div>
    </div>
  );
}
